import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from 'components/ui/sidebar';
import { UPGRADE_ACCESS_MODAL_ID } from 'consts/modals';
import { useModals } from 'contexts/modals';
import { useIsPLOnly, useTrial } from 'hooks/use-auth';
import { ChevronRight, Clock, LogIn, PanelLeft, PanelLeftClose, X } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { NavAgents, NavAgentsCollapsedItem } from './nav-agents';
import { NavApps } from './nav-apps';
import { NavUser } from './nav-user';
import { SIDEBAR_SECTION_LABEL_CLASS } from './sidebar-section-label';
import { Button } from './ui/button';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  showNavMain?: boolean;
  isAuthenticated?: boolean;
  user?: { name: string, email: string } | null;
  onLogin: () => void;
  onLogout: () => void;
}

export function AppSidebar ({ showNavMain, isAuthenticated, user, onLogin, onLogout, ...props }: AppSidebarProps) {
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
  // Inside the mobile Sheet the sidebar is always the full-width variant;
  // `state` only describes the desktop expanded/icon-rail toggle.
  const isCollapsed = state === 'collapsed' && !isMobile;
  const isPLOnly = useIsPLOnly();
  const { openModal } = useModals();
  const trial = useTrial();

  // Any navigation closes the mobile drawer, so links don't each need an
  // onClick. No-op on desktop (openMobile is unused there).
  const { pathname, search } = useLocation();
  React.useEffect(() => {
    setOpenMobile(false);
  }, [pathname, search, setOpenMobile]);

  let toggleIcon = <PanelLeftClose className="h-5 w-5" />;
  let toggleTitle = 'Collapse sidebar';
  if (isMobile) {
    toggleIcon = <X className="h-5 w-5" />;
    toggleTitle = 'Close menu';
  } else if (isCollapsed) {
    toggleIcon = <PanelLeft className="h-5 w-5" />;
    toggleTitle = 'Expand sidebar';
  }

  return (
    <Sidebar
      collapsible="icon"
      className="fixed top-0 h-screen"
      {...props}
    >
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent"
            title={toggleTitle}
          >
            {toggleIcon}
          </Button>
          {!isCollapsed && (
            <img
              src="/assets/hilo-full-logo.png"
              alt="HiLo Analytics"
              className="h-6 object-contain"
            />
          )}
        </div>
      </SidebarHeader>
      <SidebarContent className="gap-1">
        {showNavMain && (
          <>
            {isCollapsed ? (
              <SidebarGroup>
                <SidebarMenu>
                  <NavApps />
                  {!isPLOnly && <NavAgentsCollapsedItem />}
                </SidebarMenu>
              </SidebarGroup>
            ) : (
              <>
                <Collapsible defaultOpen className="group/apps shrink-0">
                  <SidebarGroup>
                    <CollapsibleTrigger asChild>
                      <SidebarGroupLabel className={`cursor-pointer flex items-center justify-between pr-0 ${SIDEBAR_SECTION_LABEL_CLASS}`}>
                        Apps
                        <span className="p-1">
                          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]/apps:rotate-90" />
                        </span>
                      </SidebarGroupLabel>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          <NavApps />
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </CollapsibleContent>
                  </SidebarGroup>
                </Collapsible>

                {!isPLOnly && <NavAgents />}
              </>
            )}
          </>
        )}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        {/* group-data-[collapsible=icon]:hidden hides the trial banner when the
            sidebar is collapsed to icon width — there's no room for the text. */}
        {!!trial && trial.isActive && (
          <div className="flex flex-col gap-2 px-2 py-1 group-data-[collapsible=icon]:hidden">
            <div className="flex items-center text-xs text-amber-500">
              <Clock className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              <span>Trial expires in {trial.daysLeft} days</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openModal(UPGRADE_ACCESS_MODAL_ID)}
            >
              Upgrade
            </Button>
          </div>
        )}
        {!isAuthenticated && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={onLogin}>
                <LogIn />
                <span>Sign In</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        {isAuthenticated && user && (
          <NavUser user={user} onLogout={onLogout} />
        )}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
